import { ApiError } from '../../api/client.js';
import { BleSetupError, validateWifi, type DeviceBleTransport, type ProvisioningInfo, type ReceiptStatus } from '../../device-ble/transport.js';
import { normalizeScreenCode, parsePossessionCode, type Possession } from '../../device-ble/initial-provision.js';
import { bleMessageFor } from '../../device-ble/messages.js';
import { envelopeNetworks, type SavedNetworks } from '../../device-ble/provision.js';

/** Reserved for future cloud telemetry. The API does not send it today, so nothing here is ever simulated. */
export interface DeviceTelemetry { firmware?: string; flashBytes?: number; psramBytes?: number; sd?: { available: boolean; capacityBytes?: number } }
/** `role` comes from the server for shared devices; a missing role is an owner (older servers). */
export type DeviceRole = 'owner' | 'member';
export interface AccountDevice { id: string; name: string; status: 'online' | 'offline' | 'revoked'; lastSeenAt: string | null; pairedAt: string; role?: DeviceRole; telemetry?: DeviceTelemetry }
export type BleTransportLike = Pick<DeviceBleTransport, 'send' | 'disconnect'>;
export interface BleLink { info: ProvisioningInfo; transport: BleTransportLike }
export interface BleChoice { deviceId: string; deviceName: string; select(): Promise<void> }
export interface BleHooks { onChoices(choices: BleChoice[]): void; onDisconnected(): void }
/** connect() must open the Bluetooth chooser synchronously: Web Bluetooth only accepts the user's click. */
export interface BleDriver {
  connect(signal: AbortSignal, hooks: BleHooks): Promise<BleLink>;
  /** Native shells keep a scan alive outside the page; this asks them to stop it. Bounded and never throws. */
  cancel?(): Promise<void>;
}
/** App foreground/background (native shells). Going to the background must drop the radio link and typed secrets. */
export interface SyncLifecycle { subscribe(listener: (foreground: boolean) => void): () => void }
/** Read-only: count already-permitted ports; never request permission or open the port. */
export interface UsbProbeResult { ports: number; connectedDeviceId: string | null }
export interface UsbProbe { detect(signal: AbortSignal): Promise<UsbProbeResult> }
export type ProvisionProgress = 'sending' | 'verifying' | 'waiting_cloud';
export interface ProvisionOutcome { saved: boolean; cloudConfirmed: boolean; status: ReceiptStatus }
export type Network = { ssid: string; psk: string } | { reuse: true } | SavedNetworks;
export interface ProvisionCall { link: BleLink; info: ProvisioningInfo; network: Network; signal: AbortSignal; onProgress(progress: ProvisionProgress): void }
export interface Provisioner {
  owned(call: ProvisionCall): Promise<ProvisionOutcome>;
  initial(call: ProvisionCall & { possession: Possession }): Promise<ProvisionOutcome>;
}
export interface DeviceSyncDeps {
  listAccount(signal: AbortSignal): Promise<AccountDevice[]>;
  ble: BleDriver | null;
  usb: UsbProbe | null;
  provisioner: Provisioner;
  lifecycle?: SyncLifecycle | null;
  now?: () => number;
}

export type SyncPhase = 'idle' | 'searching' | 'results' | 'adding' | 'added' | 'error';
export type AccountStatus = 'idle' | 'loading' | 'ready' | 'error';
export type BleStatus = 'unavailable' | 'idle' | 'searching' | 'choosing' | 'connected' | 'none' | 'error' | 'expired' | 'disconnected';
export type UsbStatus = 'unavailable' | 'idle' | 'searching' | 'none' | 'found' | 'multiple' | 'error';
export type AddStatus = 'idle' | 'working' | 'invalid' | 'error' | 'cancelled' | 'done';
export type ChipId = 'account-online' | 'account-idle' | 'revoked' | 'nearby-account' | 'nearby-new' | 'nearby-foreign' | 'nearby-unconfirmed' | 'usb' | 'added';
export interface Chip { id: ChipId; text: string }
export type EntryAction = 'add' | 'configure' | 'connect' | 'add-usb';
export type NearbyRelation = 'in-account' | 'new' | 'other-account' | 'possible-account' | 'unconfirmed';
export interface Nearby {
  via: 'link' | 'choice';
  relation: NearbyRelation;
  info: ProvisioningInfo | null;
  choice: { deviceId: string; deviceName: string } | null;
  selecting: boolean;
}
export interface SyncEntry {
  key: string;
  kind: 'account' | 'nearby' | 'usb';
  id: string | null;
  name: string;
  account: AccountDevice | null;
  nearby: Nearby | null;
  usb: { connectedId: string | null } | null;
  chips: Chip[];
  actions: EntryAction[];
  added: boolean;
  note: string | null;
}
export interface SyncState {
  phase: SyncPhase;
  /** true once Sincronizar was pressed in this view */
  synced: boolean;
  message: string;
  alert: string | null;
  account: { status: AccountStatus; devices: AccountDevice[]; error: string | null; syncedAt: number | null; revision: number };
  ble: { status: BleStatus; choices: Array<{ deviceId: string; deviceName: string }>; selecting: string | null; link: { info: ProvisioningInfo; readAt: number } | null; error: string | null };
  usb: { status: UsbStatus; ports: number; connectedDeviceId: string | null };
  add: { status: AddStatus; deviceId: string | null; progress: ProvisionProgress | null; message: string | null; cloudConfirmed: boolean | null };
  /** What Bluetooth reported in this session. Cloud and USB data never reach here. */
  bleSeen: Record<string, { hasWifi: boolean; readAt: number; source: 'info' | 'receipt' }>;
  addedId: string | null;
  entries: SyncEntry[];
}
/** `savedNetworkIds` are account networks in priority order; add() turns them into networkId or networkIds from the unit's INFO. */
export interface AddRequest { deviceId: string; network: Network | { savedNetworkIds: string[] }; possession?: string }
export interface DeviceSync {
  getState(): SyncState;
  subscribe(listener: (state: SyncState) => void): () => void;
  start(): Promise<void>;
  refreshAccount(options?: { silent?: boolean }): Promise<void>;
  refreshUsb(): Promise<void>;
  selectChoice(deviceId: string): Promise<void>;
  add(request: AddRequest): Promise<void>;
  cancel(): void;
  applyAccount(devices: AccountDevice[]): void;
  destroy(): void;
}

const WIFI_RULE = 'Revisa la red y la contraseña: entre 8 y 63 caracteres, o vacía para una red abierta.';
const SAVED_NEEDS_ACCOUNT = 'Una unidad nueva se añade escribiendo su red Wi-Fi. Cuando esté en tu cuenta podrás enviarle tus redes guardadas.';
const SCREEN_NOTE = 'Este ASIS muestra un código en su pantalla. Pulsa Añadir y escríbelo para vincularlo a tu cuenta y darle tu Wi-Fi.';
export const SCREEN_CODE_RULE = 'Escribe el código de 6 letras y números que aparece en la pantalla de ASIS.';
const SAVED_NEEDS_ONE = 'Elige al menos una de tus redes guardadas.';
const BOOT_RETRY = 'mantén BOOT 10 segundos y vuelve a intentarlo.';
/** A refused connection means different things depending on who chose the network: only typed credentials can be mistyped. */
function wifiFailedText(network: Network): string {
  if ('networkId' in network || 'networkIds' in network) {
    return 'networkIds' in network && network.networkIds.length > 1
      ? `El dispositivo no ha podido conectarse con las redes guardadas: puede que no estén al alcance o que alguna contraseña haya cambiado. Edítalas en Redes Wi-Fi o prueba otras; ${BOOT_RETRY}`
      : `El dispositivo no ha podido conectarse con la red guardada: puede que no esté al alcance o que su contraseña haya cambiado. Edítala en Redes Wi-Fi o prueba otra; ${BOOT_RETRY}`;
  }
  if ('reuse' in network) return `El dispositivo no ha podido conectarse con la red que ya tenía guardada: puede que no esté al alcance o que su contraseña haya cambiado. Prueba con otra red; ${BOOT_RETRY}`;
  return `El dispositivo no ha podido conectarse. Revisa el nombre y la contraseña, ${BOOT_RETRY}`;
}
const NO_DEVICE_TEXT = 'No se ha elegido ningún aparato por Bluetooth.';
export const MEMBER_NOTE = 'Este ASIS está compartido contigo. Solo su propietario puede cambiar la red Wi-Fi.';
const FOREIGN_NOTE = 'Este aparato indica que ya tiene propietario y no figura en tu cuenta. Entra en la cuenta propietaria o pídele una invitación para compartirlo contigo. Encontrarlo por Bluetooth no cambia su propietario.';
const BACKGROUND_TEXT = 'Configuración detenida al salir de la app. Vuelve a sincronizar cuando quieras continuar.';
const isAbort = (error: unknown): boolean => (error as { name?: string } | null)?.name === 'RequestAbortError' || (error as { name?: string } | null)?.name === 'AbortError' || (error instanceof BleSetupError && error.code === 'cancelled');

export function bleNameForId(id: string): string { return `ASIS-${id.slice(-6)}`; }
export function nameSuffix(name: string): string | null { const found = /^ASIS-([A-Za-z0-9]{6})$/u.exec(name); return found ? found[1]!.toLowerCase() : null; }

/** Spanish elapsed time. Clock skew (future timestamps) reads as "ahora". */
export function agoText(elapsedMs: number): string {
  const minutes = Math.floor(Math.max(0, elapsedMs) / 60_000);
  if (minutes < 1) return 'hace menos de 1 min';
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'hace 1 día' : `hace ${days} días`;
}

export function accountErrorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Tu sesión ha caducado. Entra de nuevo en ASIS.';
    if (error.status === 429) return 'Espera un momento antes de volver a intentarlo.';
  }
  return 'No se pudo cargar la lista de dispositivos. Comprueba la conexión a Internet y reintenta.';
}

/** Identifier match is proof; a name suffix (six characters) never is. */
export function matchNearby(nearby: { id?: string; name?: string }, accountDevices: AccountDevice[]): { kind: 'confirmed' | 'possible'; device: AccountDevice } | { kind: 'none' } {
  const active = accountDevices.filter(device => device.status !== 'revoked');
  if (nearby.id) {
    const device = active.find(item => item.id === nearby.id);
    return device ? { kind: 'confirmed', device } : { kind: 'none' };
  }
  const suffix = nearby.name ? nameSuffix(nearby.name) : null;
  if (!suffix) return { kind: 'none' };
  const candidates = active.filter(device => device.id.toLowerCase().endsWith(suffix));
  return candidates.length === 1 ? { kind: 'possible', device: candidates[0]! } : { kind: 'none' };
}

function accountChip(device: AccountDevice, now: number): Chip {
  if (device.status === 'revoked') return { id: 'revoked', text: 'Revocado' };
  if (device.status === 'offline') return { id: 'account-idle', text: 'En tu cuenta · sin actividad reciente' };
  const seen = device.lastSeenAt ? Date.parse(device.lastSeenAt) : Number.NaN;
  return { id: 'account-online', text: Number.isFinite(seen) ? `En tu cuenta · activo ${agoText(now - seen)}` : 'En tu cuenta · actividad reciente' };
}

function memberOf(match: ReturnType<typeof matchNearby>): boolean { return match.kind !== 'none' && match.device.role === 'member'; }

type EntryInput = Pick<SyncState, 'account' | 'ble' | 'usb' | 'addedId'> & { add: Pick<SyncState['add'], 'status'> };
export function buildEntries(state: EntryInput, now: number): SyncEntry[] {
  const accountReady = state.account.status === 'ready';
  const entries = new Map<string, SyncEntry>();
  const blank = (key: string, kind: SyncEntry['kind'], id: string | null, name: string): SyncEntry =>
    ({ key, kind, id, name, account: null, nearby: null, usb: null, chips: [], actions: [], added: false, note: null });
  for (const device of state.account.devices) {
    const entry = blank(`id:${device.id}`, 'account', device.id, device.name);
    entry.account = device; entries.set(entry.key, entry);
  }
  const working = state.add.status === 'working';
  const link = state.ble.link?.info ?? null;
  if (link) {
    const key = `id:${link.id}`;
    const match = matchNearby({ id: link.id }, state.account.devices);
    const relation: NearbyRelation = match.kind === 'confirmed' ? 'in-account' : !accountReady ? 'unconfirmed' : link.owned ? 'other-account' : 'new';
    const entry = entries.get(key) ?? blank(key, 'nearby', link.id, bleNameForId(link.id));
    entry.nearby = { via: 'link', relation, info: link, choice: null, selecting: false };
    if (!working && state.ble.status === 'connected') {
      if (relation === 'new') entry.actions.push('add');
      if (relation === 'in-account' && memberOf(match)) entry.note = MEMBER_NOTE;
      else if (relation === 'in-account') entry.actions.push('configure');
    }
    entries.set(key, entry);
  } else {
    for (const choice of state.ble.choices) {
      const match = accountReady ? matchNearby({ name: choice.deviceName }, state.account.devices) : { kind: 'none' as const };
      const selecting = state.ble.selecting === choice.deviceId;
      const nearby: Nearby = { via: 'choice', relation: match.kind === 'possible' ? 'possible-account' : 'unconfirmed', info: null, choice: { deviceId: choice.deviceId, deviceName: choice.deviceName }, selecting };
      const target = match.kind === 'possible' ? entries.get(`id:${match.device.id}`) : undefined;
      if (target && !target.nearby) {
        target.nearby = nearby;
        target.note = `${choice.deviceName} podría ser este aparato: coincide solo el final de su nombre. Conéctalo para confirmarlo.`;
        if (state.ble.selecting === null) target.actions.push('connect');
      } else {
        const entry = blank(`choice:${choice.deviceId}`, 'nearby', null, choice.deviceName);
        entry.nearby = nearby;
        entry.note = 'Solo se conoce su nombre Bluetooth. Conéctalo para ver si ya está en tu cuenta.';
        if (state.ble.selecting === null) entry.actions.push('connect');
        entries.set(entry.key, entry);
      }
    }
  }
  if (state.usb.status === 'found') {
    const id = state.usb.connectedDeviceId;
    const key = id ? `id:${id}` : 'usb';
    const entry = entries.get(key) ?? blank(key, 'usb', id, id ? bleNameForId(id) : 'Placa ASIS por USB');
    entry.usb = { connectedId: id };
    const bleCannot = !state.ble.link && state.ble.choices.length === 0 && ['unavailable', 'none', 'error', 'expired', 'disconnected', 'idle'].includes(state.ble.status);
    if (!entry.account && bleCannot) entry.actions.push('add-usb');
    // An unidentified port is not evidence of which unit it is: after a Bluetooth add, say so rather than guess.
    if (!id) entry.note = `Puerto USB permitido. Su identificador no se lee hasta abrir el diagnóstico USB.${state.addedId ? ' Si es la misma placa que acabas de añadir, no hace falta añadirla otra vez.' : ''}`;
    entries.set(key, entry);
  }
  for (const entry of entries.values()) {
    if (entry.account) entry.chips.push(accountChip(entry.account, now));
    const near = entry.nearby;
    if (near) {
      const text = near.relation === 'in-account' ? 'Cerca · ya está en tu cuenta' : near.relation === 'new' ? 'Cerca · sin añadir'
        : near.relation === 'other-account' ? 'Cerca · no pertenece a tu cuenta' : 'Cerca · sin confirmar';
      const id: ChipId = near.relation === 'in-account' ? 'nearby-account' : near.relation === 'new' ? 'nearby-new' : near.relation === 'other-account' ? 'nearby-foreign' : 'nearby-unconfirmed';
      entry.chips.push({ id, text });
      if (near.relation === 'other-account') entry.note = FOREIGN_NOTE;
      if (near.relation === 'new' && near.info?.codeCheck) entry.note = SCREEN_NOTE;
      if (near.relation === 'unconfirmed' && near.via === 'link') entry.note = 'No se pudo comprobar tu cuenta, así que no se puede confirmar a quién pertenece.';
    }
    if (entry.usb) entry.chips.push({ id: 'usb', text: 'Por USB' });
    if (state.addedId && entry.id === state.addedId && entry.account) { entry.added = true; entry.chips.push({ id: 'added', text: 'Añadido' }); }
  }
  const all = [...entries.values()];
  // What was just found or added comes first; the rest of the account keeps server order.
  const found = (entry: SyncEntry) => !entry.account || entry.added ? 0 : entry.nearby || entry.usb ? 1 : 2;
  return [0, 1, 2].flatMap(rank => all.filter(entry => found(entry) === rank));
}

export function describeSources(state: Pick<SyncState, 'account' | 'ble' | 'usb'>): { account: string; ble: string; usb: string } {
  const count = state.account.devices.filter(device => device.status !== 'revoked').length;
  const account = state.account.status === 'loading' ? 'cargando…' : state.account.status === 'error' ? 'no se pudo cargar'
    : state.account.status === 'idle' ? 'sin cargar' : count === 0 ? 'sin aparatos' : count === 1 ? '1 aparato' : `${count} aparatos`;
  const ble = ({
    unavailable: 'no disponible en este navegador', idle: 'sin buscar', searching: 'buscando…',
    choosing: state.ble.selecting ? 'conectando con el aparato elegido…' : 'elige tu aparato en la lista',
    connected: 'aparato encontrado', none: 'no se eligió ningún aparato', error: 'no se pudo conectar',
    expired: 'ventana de configuración terminada', disconnected: 'aparato desconectado',
  } satisfies Record<BleStatus, string>)[state.ble.status];
  const usb = ({
    unavailable: 'no disponible en este navegador', idle: 'sin comprobar', searching: 'comprobando…', none: 'sin puerto autorizado; el diagnóstico USB lo solicita',
    found: 'placa detectada', multiple: 'varios puertos permitidos; deja conectado uno', error: 'no se pudo comprobar',
  } satisfies Record<UsbStatus, string>)[state.usb.status];
  return { account, ble, usb };
}

function summary(state: SyncState): string {
  const nearbyNew = state.entries.filter(entry => entry.nearby?.relation === 'new').length;
  const known = state.account.devices.filter(device => device.status !== 'revoked').length;
  const parts = [known === 0 ? 'Tu cuenta no tiene aparatos' : known === 1 ? '1 aparato en tu cuenta' : `${known} aparatos en tu cuenta`];
  if (nearbyNew) parts.push(nearbyNew === 1 ? '1 cerca sin añadir' : `${nearbyNew} cerca sin añadir`);
  else if (state.entries.some(entry => entry.nearby)) parts.push('aparato cerca');
  if (state.usb.status === 'found') parts.push('placa por USB');
  const tail = state.ble.status === 'unavailable' ? ' Bluetooth no está disponible en este navegador: solo se muestra tu cuenta.'
    : state.ble.status === 'none' ? ` ${NO_DEVICE_TEXT}` : '';
  return `Listo. ${parts.join('; ')}.${tail}`;
}

export function createDeviceSync(deps: DeviceSyncDeps): DeviceSync {
  const now = deps.now ?? (() => Date.now());
  const lifetime = new AbortController();
  const listeners = new Set<(state: SyncState) => void>();
  let destroyed = false;
  let foreground = true;
  let bleTouched = false;
  let searching = false;
  let hasSynced = false;
  let everStarted = false;
  let searchEpoch = 0, addEpoch = 0, refreshEpoch = 0;
  let searchCtl: AbortController | null = null, addCtl: AbortController | null = null, refreshCtl: AbortController | null = null;
  let link: BleLink | null = null;
  let choices = new Map<string, BleChoice>();
  let expiry: ReturnType<typeof setTimeout> | undefined;
  let override: string | null = null;
  let state = {
    phase: 'idle', synced: false, message: 'Pulsa Sincronizar para buscar tus aparatos.', alert: null,
    account: { status: 'idle', devices: [], error: null, syncedAt: null, revision: 0 },
    ble: { status: deps.ble ? 'idle' : 'unavailable', choices: [], selecting: null, link: null, error: null },
    usb: { status: deps.usb ? 'idle' : 'unavailable', ports: 0, connectedDeviceId: null },
    add: { status: 'idle', deviceId: null, progress: null, message: null, cloudConfirmed: null },
    bleSeen: {}, addedId: null, entries: [],
  } as SyncState;

  function derive(): void {
    const adding = state.add.status === 'working';
    const phase: SyncPhase = adding ? 'adding' : searching ? 'searching' : state.account.status === 'error' ? 'error'
      : state.addedId && state.add.status === 'done' ? 'added' : hasSynced ? 'results' : 'idle';
    const alert = state.account.error ?? state.ble.error ?? (state.add.status === 'error' ? state.add.message : null);
    const entries = buildEntries(state, now());
    let message = override;
    if (message === null) {
      if (phase === 'searching') message = 'Sincronizando: cargando tu cuenta y buscando aparatos cerca…';
      else if (phase === 'error') message = 'La lista de aparatos no está al día.';
      else if (phase === 'results' || phase === 'added') message = summary({ ...state, entries });
      else message = 'Pulsa Sincronizar para buscar tus aparatos.';
    }
    state = { ...state, phase, synced: everStarted, alert, message, entries };
  }
  function set(change: Partial<SyncState>, announce?: string): void {
    if (destroyed) return;
    if (announce !== undefined) override = announce;
    state = { ...state, ...change };
    derive();
    for (const listener of [...listeners]) listener(state);
  }
  const clearExpiry = () => { if (expiry !== undefined) { clearTimeout(expiry); expiry = undefined; } };
  function dropLink(): void {
    clearExpiry();
    const current = link; link = null;
    try { current?.transport.disconnect(); } catch { /* the radio may already be gone */ }
  }
  function stopAll(): void {
    searchEpoch++; addEpoch++; refreshEpoch++;
    searchCtl?.abort(); addCtl?.abort(); refreshCtl?.abort();
    searchCtl = addCtl = refreshCtl = null;
    searching = false;
    choices = new Map();
    dropLink();
    // A native scan outlives the page's own abort; ask the shell to stop it. Nothing to stop if Bluetooth never started.
    if (bleTouched) { bleTouched = false; try { void deps.ble?.cancel?.().catch(() => undefined); } catch { /* best effort */ } }
  }

  async function loadAccount(signal: AbortSignal, live: () => boolean, quiet: boolean): Promise<void> {
    if (!quiet) set({ account: { ...state.account, status: 'loading', error: null } });
    try {
      const devices = await deps.listAccount(signal);
      if (!live()) return;
      set({ account: { status: 'ready', devices: devices.map(device => ({ ...device })), error: null, syncedAt: now(), revision: state.account.revision + 1 } });
    } catch (error) {
      if (!live() || isAbort(error)) return;
      set({ account: { ...state.account, status: 'error', error: accountErrorText(error) } });
    }
  }
  async function connectBle(signal: AbortSignal, live: () => boolean): Promise<void> {
    const driver = deps.ble;
    if (!driver) return;
    bleTouched = true;
    set({ ble: { status: 'searching', choices: [], selecting: null, link: null, error: null } });
    try {
      const found = await driver.connect(signal, {
        onChoices: next => {
          if (!live() || link) return;
          choices = new Map(next.map(choice => [choice.deviceId, choice]));
          // An empty list means the chooser is open but nothing was heard yet: still searching.
          set({ ble: { ...state.ble, status: next.length ? 'choosing' : 'searching', choices: next.map(choice => ({ deviceId: choice.deviceId, deviceName: choice.deviceName })) } });
        },
        onDisconnected: () => {
          if (!live() || state.add.status === 'working' || !link) return;
          dropLink();
          set({ ble: { status: 'disconnected', choices: [], selecting: null, link: null, error: null } });
        },
      });
      if (!live()) { try { found.transport.disconnect(); } catch { /* ignore */ } return; }
      link = found; choices = new Map();
      const readAt = now();
      set({ ble: { status: 'connected', choices: [], selecting: null, link: { info: found.info, readAt }, error: null }, bleSeen: { ...state.bleSeen, [found.info.id]: { hasWifi: found.info.hasWifi, readAt, source: 'info' } } });
      clearExpiry();
      expiry = setTimeout(() => {
        expiry = undefined;
        if (destroyed || !link || state.add.status === 'working') return;
        dropLink();
        set({ ble: { status: 'expired', choices: [], selecting: null, link: null, error: null } });
      }, Math.max(1, found.info.expiresIn * 1000));
    } catch (error) {
      if (!live()) return;
      choices = new Map();
      const code = error instanceof BleSetupError ? error.code : null;
      const name = (error as { name?: string } | null)?.name;
      // The chooser closing, no device selected and a 90 s timeout look alike to the page: none of them is a failure.
      if (name === 'NotFoundError' || code === 'cancelled' || code === 'timeout') set({ ble: { status: 'none', choices: [], selecting: null, link: null, error: null } });
      else set({ ble: { status: 'error', choices: [], selecting: null, link: null, error: bleMessageFor(error) } });
    }
  }
  async function probeUsb(signal: AbortSignal, live: () => boolean): Promise<void> {
    const probe = deps.usb;
    if (!probe) return;
    set({ usb: { status: 'searching', ports: 0, connectedDeviceId: null } });
    try {
      const found = await probe.detect(signal);
      if (!live()) return;
      const status: UsbStatus = found.connectedDeviceId || found.ports === 1 ? 'found' : found.ports > 1 ? 'multiple' : 'none';
      set({ usb: { status, ports: found.ports, connectedDeviceId: found.connectedDeviceId } });
    } catch (error) {
      if (!live() || isAbort(error)) return;
      set({ usb: { status: 'error', ports: 0, connectedDeviceId: null } });
    }
  }

  async function start(): Promise<void> {
    if (destroyed || !foreground || searching || state.add.status === 'working') return;
    stopAll();
    const my = ++searchEpoch;
    const ctl = new AbortController(); searchCtl = ctl;
    const signal = AbortSignal.any([lifetime.signal, ctl.signal]);
    const live = () => !destroyed && my === searchEpoch && !signal.aborted;
    searching = true; hasSynced = false; everStarted = true; override = null;
    // The three sources start in this tick, in this order, so Web Bluetooth still sees the click.
    set({
      addedId: null, add: { status: 'idle', deviceId: null, progress: null, message: null, cloudConfirmed: null },
      ble: { status: deps.ble ? 'searching' : 'unavailable', choices: [], selecting: null, link: null, error: null },
      usb: { status: deps.usb ? 'searching' : 'unavailable', ports: 0, connectedDeviceId: null },
    });
    const sources = [loadAccount(signal, live, false), connectBle(signal, live), probeUsb(signal, live)];
    await Promise.allSettled(sources);
    if (!live()) return;
    searching = false; hasSynced = true; searchCtl = null;
    set({});
  }

  async function refreshAccount(options: { silent?: boolean } = {}): Promise<void> {
    if (destroyed || searching || state.add.status === 'working') return;
    refreshCtl?.abort();
    const my = ++refreshEpoch;
    const ctl = new AbortController(); refreshCtl = ctl;
    const signal = AbortSignal.any([lifetime.signal, ctl.signal]);
    const live = () => !destroyed && my === refreshEpoch && !signal.aborted && !searching && state.add.status !== 'working';
    await loadAccount(signal, live, Boolean(options.silent) && state.account.status === 'ready');
    if (my === refreshEpoch) refreshCtl = null;
  }

  async function refreshUsb(): Promise<void> {
    if (destroyed || !everStarted || searching) return;
    const my = searchEpoch;
    const live = () => !destroyed && my === searchEpoch && !searching;
    await probeUsb(lifetime.signal, live);
  }

  async function selectChoice(deviceId: string): Promise<void> {
    const choice = choices.get(deviceId);
    if (destroyed || !choice || state.ble.selecting !== null || state.ble.status !== 'choosing') return;
    const my = searchEpoch;
    set({ ble: { ...state.ble, selecting: deviceId, error: null } });
    try { await choice.select(); }
    catch {
      if (destroyed || my !== searchEpoch) return;
      set({ ble: { ...state.ble, selecting: null, error: 'La búsqueda ha terminado. Vuelve a pulsar Sincronizar.' } });
    }
  }

  async function add(request: AddRequest): Promise<void> {
    const wipe = () => { if ('psk' in request.network) request.network.psk = ''; };
    const refuse = (message: string) => { wipe(); set({ add: { status: 'invalid', deviceId: request.deviceId, progress: null, message, cloudConfirmed: null } }); };
    if (destroyed || !foreground || searching || state.add.status === 'working' || !link || state.ble.status !== 'connected' || link.info.id !== request.deviceId) { wipe(); return; }
    override = null;
    if (state.account.status !== 'ready') { refuse('No se pudo comprobar tu cuenta. Pulsa Reintentar antes de añadir el aparato.'); return; }
    const current = link;
    const match = matchNearby({ id: current.info.id }, state.account.devices);
    const inAccount = match.kind === 'confirmed';
    // Only the owner changes the network of a shared unit, and nothing is sent to find out.
    if (memberOf(match)) { refuse(MEMBER_NOTE); return; }
    // The authenticated account list wins over a device's stale INFO hint.
    const info: ProvisioningInfo = { ...current.info, owned: inAccount };
    let proof: Possession | null = null;
    // ASIS One: an unowned unit shows a code on its screen; that code (not a label) proves you have it in front of you.
    const screen = !inAccount && Boolean(current.info.codeCheck);
    if (!inAccount) {
      if (current.info.owned) { refuse('Este aparato ya tiene propietario y no figura en tu cuenta. Entra en la cuenta propietaria.'); return; }
      if (screen) {
        const code = normalizeScreenCode(request.possession ?? '');
        if (!code) { refuse(SCREEN_CODE_RULE); return; }
        proof = { code };
      } else {
        const parsed = parsePossessionCode(request.possession ?? '', info.id);
        if (!parsed) { refuse('El código privado no es válido para esta unidad. Revisa su etiqueta.'); return; }
        proof = { proof: parsed };
      }
    }
    let network: Network;
    if ('savedNetworkIds' in request.network) {
      // A factory unit is claimed with a typed network: its claim route does not read the account's vault.
      if (!inAccount && !screen) { refuse(SAVED_NEEDS_ACCOUNT); return; }
      const selection = envelopeNetworks(request.network.savedNetworkIds, info);
      if (!selection) { refuse(SAVED_NEEDS_ONE); return; }
      network = selection;
    } else network = request.network;
    if ('reuse' in network) {
      if (!inAccount || !info.hasWifi) { refuse('Este aparato no tiene una red guardada que mantener. Indica la red Wi-Fi.'); return; }
    } else if ('ssid' in network && !validateWifi(network.ssid, network.psk)) { refuse(WIFI_RULE); return; }

    clearExpiry();
    const my = ++addEpoch;
    const ctl = new AbortController(); addCtl = ctl;
    const signal = AbortSignal.any([lifetime.signal, ctl.signal]);
    const live = () => !destroyed && my === addEpoch && !signal.aborted;
    const progressText = { sending: 'Enviando la configuración protegida…', verifying: 'Verificando la respuesta del dispositivo…', waiting_cloud: 'Wi-Fi guardada. Esperando a que ASIS contacte con tu cuenta…' } satisfies Record<ProvisionProgress, string>;
    set({ add: { status: 'working', deviceId: info.id, progress: 'sending', message: null, cloudConfirmed: null } }, progressText.sending);
    const onProgress = (progress: ProvisionProgress) => { if (live()) set({ add: { ...state.add, progress } }, progressText[progress]); };
    const call: ProvisionCall = { link: current, info, network, signal, onProgress };
    let outcome: ProvisionOutcome | null = null, failure: unknown = null;
    try { outcome = proof ? await deps.provisioner.initial({ ...call, possession: proof }) : await deps.provisioner.owned(call); }
    catch (error) { failure = error; }
    finally { wipe(); if (proof) { if ('code' in proof) proof.code = ''; else proof.proof = ''; } if (my === addEpoch) { addCtl = null; if (link === current) dropLink(); } }
    if (!live()) return;
    if (failure || !outcome) {
      set({ add: { status: 'error', deviceId: info.id, progress: null, message: bleMessageFor(failure), cloudConfirmed: null }, ble: { status: 'idle', choices: [], selecting: null, link: null, error: null } }, 'No hemos podido confirmar toda la configuración.');
      return;
    }
    if (!outcome.saved) {
      const text = outcome.status === 'wifi_failed'
        ? wifiFailedText(network)
        : 'El dispositivo no ha podido guardar la configuración. Vuelve a abrir el modo de configuración para intentarlo.';
      set({ add: { status: 'error', deviceId: info.id, progress: null, message: text, cloudConfirmed: null }, ble: { status: 'idle', choices: [], selecting: null, link: null, error: null } }, 'No se ha guardado la nueva red.');
      return;
    }
    const seen = { ...state.bleSeen, [info.id]: { hasWifi: true, readAt: now(), source: 'receipt' as const } };
    set({ add: { status: 'done', deviceId: info.id, progress: null, message: null, cloudConfirmed: outcome.cloudConfirmed }, ble: { status: 'idle', choices: [], selecting: null, link: null, error: null }, bleSeen: seen, addedId: info.id, account: { ...state.account, status: 'loading' } }, 'Aparato configurado. Actualizando tu cuenta…');
    try {
      const devices = await deps.listAccount(signal);
      if (!live()) return;
      set({ account: { status: 'ready', devices: devices.map(device => ({ ...device })), error: null, syncedAt: now(), revision: state.account.revision + 1 } });
    } catch (error) {
      if (!live() || isAbort(error)) return;
      set({ account: { ...state.account, status: 'error', error: 'El aparato se configuró, pero no se pudo recargar tu cuenta. Pulsa Reintentar.' } }, 'Aparato configurado; falta recargar tu cuenta.');
      return;
    }
    const added = state.account.devices.find(device => device.id === info.id && device.status !== 'revoked');
    const label = added?.name ?? bleNameForId(info.id);
    const text = !added ? 'Wi-Fi guardada y verificada, pero el aparato todavía no aparece en tu cuenta. Actualiza la lista en unos instantes.'
      : outcome.cloudConfirmed ? `${label} añadido. ASIS conectado a Internet y a tu cuenta.`
        : `${label}: Wi-Fi guardada y verificada. Todavía no se ha confirmado un nuevo contacto con tu cuenta.`;
    set({}, text);
  }

  function cancel(): void { halt('user'); }

  /** 'background' also drops a radio link that is only waiting for the user, so no secret outlives the foreground. */
  function halt(reason: 'user' | 'background'): void {
    if (destroyed) return;
    const wasAdding = state.add.status === 'working';
    const wasSearching = searching;
    const heldLink = link !== null || choices.size > 0;
    if (!wasAdding && !wasSearching && !(reason === 'background' && heldLink)) return;
    stopAll();
    override = null;
    const accountStatus: AccountStatus = state.account.status === 'loading' ? (state.account.syncedAt === null ? 'idle' : 'ready') : state.account.status;
    set({
      account: { ...state.account, status: accountStatus },
      ble: { status: deps.ble ? 'idle' : 'unavailable', choices: [], selecting: null, link: null, error: null },
      usb: state.usb.status === 'searching' ? { status: deps.usb ? 'idle' : 'unavailable', ports: 0, connectedDeviceId: null } : state.usb,
      add: wasAdding
        ? { status: 'cancelled', deviceId: state.add.deviceId, progress: null, message: 'Comprobación cancelada. Si la red ya se guardó, el dispositivo podrá seguir conectándose a ella.', cloudConfirmed: null }
        : state.add,
    }, reason === 'background' ? BACKGROUND_TEXT : wasAdding ? 'Configuración cancelada.' : 'Búsqueda cancelada. Puedes volver a sincronizar cuando quieras.');
  }

  const unlisten = deps.lifecycle?.subscribe(active => {
    if (destroyed) return;
    foreground = active;
    if (!active) halt('background');
    else set({});
  });

  function applyAccount(devices: AccountDevice[]): void {
    if (destroyed) return;
    set({ account: { status: state.account.status === 'loading' ? 'loading' : 'ready', devices: devices.map(device => ({ ...device })), error: null, syncedAt: state.account.syncedAt ?? now(), revision: state.account.revision + 1 } });
  }

  function destroy(): void {
    if (destroyed) return;
    stopAll();
    destroyed = true;
    unlisten?.();
    lifetime.abort();
    listeners.clear();
  }

  derive();
  return {
    getState: () => state,
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    start, refreshAccount, refreshUsb, selectChoice, add, cancel, applyAccount, destroy,
  };
}
